'use client';

import * as React from 'react';
import { Spinner } from '../ui';

type InViewTriggerProps = {
  /** 뷰포트에 들어올 때 호출됩니다. */
  onEnter: () => void | Promise<void>;
  /** (선택) 뷰포트에서 벗어날 때 호출됩니다. */
  onLeave?: () => void | Promise<void>;
  /** 한 번만 실행할지 여부 (기본값: true) */
  once?: boolean;
  /** IntersectionObserver 옵션들 */
  root?: Element | Document | null;
  rootMargin?: string;
  threshold?: number | number[];
  /** 로딩 스피너 표시 여부 (기본값: true) */
  showSpinner?: boolean;
  /** 래퍼 클래스 */
  className?: string;
  /** 트리거 영역 안에 children을 넣고 싶을 때 */
  children?: React.ReactNode;
};

export function InViewTrigger({
  onEnter,
  onLeave,
  once = false,
  root = null,
  rootMargin = '0px',
  threshold = 0.1,
  showSpinner = true,
  className,
  children,
}: Readonly<InViewTriggerProps>) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const triggeredRef = React.useRef(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      async (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          if (!once || (once && !triggeredRef.current)) {
            triggeredRef.current = true;
            await onEnter?.();
            if (!once) {
              // once=false면 다시 나갔다 들어올 때 재호출 가능
              triggeredRef.current = false;
            }
          }
        } else {
          // 뷰포트에서 벗어남
          onLeave?.();
        }
      },
      { root, rootMargin, threshold }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [onEnter, onLeave, once, root, rootMargin, threshold]);

  return (
    <div
      aria-busy={showSpinner ? true : undefined}
      aria-live="polite"
      className={['relative flex items-center justify-center', className].filter(Boolean).join(' ')}
      ref={ref}
    >
      {children ??
        (showSpinner && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Spinner
              size="sm"
              variant="muted"
            />
            <span className="sr-only">로딩 중…</span>
          </div>
        ))}
    </div>
  );
}
